import React from 'react';
import { useService } from '../../../../../hooks';
import FileService, {
  type GetFileResponse,
} from '../../../../../services/FileService';
import { useReportErrorToBugsnag } from '../../../../../util/bugsnagReporting';
import {
  type KnowledgeBaseCitation,
  isCsvKnowledgeBaseFile,
} from '../../../../../util/knowledgeBaseCitation';

const fileService = new FileService();

export type ResolvedCsv = {
  /** Pre-signed URL we'll fetch CSV bytes from. */
  url: string;
  /** Pre-signed Content-Disposition: attachment URL for the download button. */
  downloadUrl: string;
  contentType?: string;
  fileName: string;
};

export type ResolveKnowledgeBaseCsvState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'success'; resolved: ResolvedCsv }
  | { status: 'not-csv' }
  | { status: 'error'; error: Error };

/**
 * Resolves a knowledge-base citation to download URLs suitable for the in-app
 * CSV viewer. Use for inline embedding (no click required); for click-driven
 * "open this file" interactions use {@link useOpenKnowledgeBaseFile} instead.
 *
 * Refuses to resolve non-CSV files (returns `not-csv`) so callers don't end up
 * fetching arbitrary files into memory.
 */
export const useResolveKnowledgeBaseCsv = (
  citation: KnowledgeBaseCitation,
  options: { enabled?: boolean } = {},
): ResolveKnowledgeBaseCsvState => {
  const { enabled = true } = options;
  const reportErrorToBugsnag = useReportErrorToBugsnag();

  const [fetchFiles] = useService(fileService.fetchFiles);
  const [fetchOpenLink] = useService(fileService.fetchOpenLink);
  const [fetchDownloadLink] = useService(fileService.fetchDownloadLink);

  const [state, setState] = React.useState<ResolveKnowledgeBaseCsvState>(
    enabled ? { status: 'loading' } : { status: 'idle' },
  );

  React.useEffect(() => {
    if (!enabled) {
      setState({ status: 'idle' });
      return;
    }

    let cancelled = false;
    setState({ status: 'loading' });

    (async () => {
      try {
        const list = await fetchFiles({
          datasetId: citation.projectId,
          name: citation.name,
          pageSize: 1,
        });
        if (cancelled) return;

        const file = list.data?.items?.find(
          (item: GetFileResponse) => item.name === citation.name,
        );
        if (!file?.id) {
          throw new Error(
            `Knowledge base file not found for citation: ${citation.name}`,
          );
        }
        const open = await fetchOpenLink(file.id);
        if (cancelled) return;

        const url = open.data?.downloadUrl;
        if (!url) {
          throw new Error('Knowledge base file open link returned no url');
        }
        const contentType = open.data?.contentType || file.contentType;

        if (
          !isCsvKnowledgeBaseFile({ mime: contentType, name: citation.name })
        ) {
          setState({ status: 'not-csv' });
          return;
        }

        let downloadUrl = url;
        try {
          const dl = await fetchDownloadLink(file.id);
          if (dl.data?.downloadUrl) {
            downloadUrl = dl.data.downloadUrl;
          }
        } catch (e) {
          // Non-fatal: caller can still hit the open URL to grab the bytes.
        }

        if (cancelled) return;
        setState({
          status: 'success',
          resolved: {
            url,
            downloadUrl,
            contentType,
            fileName: citation.displayName,
          },
        });
      } catch (e) {
        if (cancelled) return;
        const error = e instanceof Error ? e : new Error(String(e));
        reportErrorToBugsnag(error, {
          metaData: {
            knowledgeBase: {
              projectId: citation.projectId,
              name: citation.name,
            },
          },
        });
        setState({ status: 'error', error });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    enabled,
    citation.displayName,
    citation.name,
    citation.projectId,
    fetchDownloadLink,
    fetchFiles,
    fetchOpenLink,
    reportErrorToBugsnag,
  ]);

  return state;
};
